import { useState, type FormEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { updateDisplayName } from '../../auth/authApi.ts';
import { useAuth } from '../../auth/useAuth.ts';
import { describeError } from '../../ui/problemDetails.ts';
import { groupKeys } from '../groups/groupsApi.ts';

/**
 * Change the name shown next to the things you tick off.
 *
 * Worth having reachable: the name is chosen at sign-up, before you have any idea it
 * will be sitting beside every item you pick up in front of the people you live with.
 *
 * A section rather than a page of its own, so ProfilePage can put the rest of the account
 * around it — but it belongs to the person, not to a group, which is why it is no longer
 * in the group settings panel.
 */
export function ProfileSettings() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const [name, setName] = useState(user?.displayName ?? '');

  const save = useMutation({
    mutationFn: updateDisplayName,
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['currentUser'] }),
        // Groups embed the display name in their member list, so they are stale now.
        queryClient.invalidateQueries({ queryKey: groupKeys.all }),
      ]);
    },
  });

  const trimmed = name.trim();

  function handleSubmit(event: FormEvent) {
    event.preventDefault();

    if (trimmed.length > 0) {
      save.mutate(trimmed);
    }
  }

  return (
    <section className="settings-section">
      <h3 className="settings-section__title">Display name</h3>

      <form className="settings-row" onSubmit={handleSubmit}>
        <input
          className="settings-row__input"
          value={name}
          onChange={(event) => setName(event.target.value)}
          aria-label="Your display name"
          maxLength={80}
          required
        />
        <button
          className="button button--primary button--small"
          type="submit"
          disabled={save.isPending || trimmed === user?.displayName || trimmed.length === 0}
        >
          Save
        </button>
      </form>

      <p className="settings-hint">Shown next to everything you tick off.</p>

      {save.isError && (
        <p className="form-error" role="alert">
          {describeError(save.error)}
        </p>
      )}

      {save.isSuccess && !save.isPending && <p className="settings-status">Saved.</p>}
    </section>
  );
}
